import { useState } from 'react';
import TeletLogo from 'src/assets/logo/tellet-logo-main.svg';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { NavLink } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { ERROR_MESSAGES } from '@common/constants';
import { StatusCodes } from 'http-status-codes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import useAuthStore from '@/store/AuthStore';
import { loginValidationSchema } from '@/utils/validationSchemas';
import { useMutateResendEmailInvitation } from '@/reactQuery/post';
import TwoFactorVerificationForm from '@/components/forms/TwoFactorVerificationForm';
import useAuthentication from '@/hooks/useAuthentication';
import ToastMessage from '@/components/toast-message/ToastMessage';
import { CloudflareTurnstileWidget } from './CloudflareTunrstileWidget';
import { CloudflareAction } from './CloudflareUtils';

const defaultValues = {
  email: '',
  password: '',
};

type TLoginForm = typeof defaultValues;

const EmailError = {
  [StatusCodes.UNAUTHORIZED]: 'Invalid email or password',
  [StatusCodes.INTERNAL_SERVER_ERROR]: 'Server error occurred, please try again later',
  [StatusCodes.FORBIDDEN]: '',
  [StatusCodes.UNPROCESSABLE_ENTITY]: 'Email already registered',
  [StatusCodes.TOO_MANY_REQUESTS]: 'Too many login attempts, please try again later',
  [ERROR_MESSAGES.EMAIL_UNVERIFIED]: 'Please verify your email to continue',
};

function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(loginValidationSchema) });
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isTurnstileValid, setIsTurnstileValid] = useState(false);

  // Custom auth hook
  const { processAuthSuccess, handleTwoFactorRequired, navigate } = useAuthentication();

  // 2FA related state from auth store
  const { requireTwoFactor, setTwoFactorUserId } = useAuthStore();

  // Email resend mutation
  const { mutateAsync: mutateAsyncResend, isPending } = useMutateResendEmailInvitation();

  const handleResendEmail = (email: string) => {
    mutateAsyncResend(email, {
      onSuccess: () => {
        toast.success(
          <ToastMessage
            title="Email Sent"
            description={`We've sent an email to ${email}. Please check your inbox.`}
            isAuthScreen
          />,
        );
      },
      onError: () => {
        toast.error(
          <ToastMessage
            title="Error"
            description="Failed to send verification email. Please try again later."
            isAuthScreen
          />,
        );
      },
    });
  };

  // Login cookies are now managed by the useAuthentication hook

  // eslint-disable-next-line consistent-return
  const onSubmit: SubmitHandler<TLoginForm> = async (formData: TLoginForm) => {
    try {
      const lowercaseEmail = formData.email.toLowerCase();
      const res = await axiosInstanceNoAuth.post('/users/login', {
        ...formData,
        email: lowercaseEmail,
      });
      const { data } = res;

      // Check if 2FA is required
      if (data.requires2FA) {
        // Store user ID in local state and global auth store
        setTwoFactorUserId(data.userId);
        handleTwoFactorRequired(data.userId);
        setLoginError('');
        return;
      }
      await processAuthSuccess(data.token, rememberMe);
      setLoginError('');
    } catch (err) {
      const error = err as AxiosError;
      const status = error.status as unknown as keyof typeof EmailError;
      if (status === StatusCodes.FORBIDDEN) {
        await handleResendEmail(formData.email);
        navigate(`${Routes.check_your_email.path}?email=${formData.email}`);
      }
      // Check for email verification error message
      if (
        status === StatusCodes.UNAUTHORIZED &&
        error.data?.message === ERROR_MESSAGES.EMAIL_UNVERIFIED
      ) {
        setLoginError(EmailError[ERROR_MESSAGES.EMAIL_UNVERIFIED]);
      } else {
        setLoginError(EmailError[status]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled = isSubmitting || isLoading || isPending || !isTurnstileValid;

  return (
    <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
      <div className="w-full max-w-[550px] min-h-screen center border-0 py-4 px-3 min450:max-w-full min450:bg-white">
        <div className="w-full max-w-[550px] flex justify-center">
          <div
            className="flex flex-col items-center w-full ring-1 ring-neutral-200 max-w-[500px] gap-10
           bg-white min450:ring-transparent min450:shadow-none rounded-md shadow-md h-fit p-7 min450:p-5"
          >
            <Image effect="blur" src={TeletLogo} alt="logo" width={120} height={40} />
            {requireTwoFactor ? (
              // Show 2FA verification form if required - using userId from global store
              <TwoFactorVerificationForm />
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col items-start w-full gap-5 h-fit"
              >
                <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                  Email
                  <Input {...register('email')} hasError={!!errors.email} type="text" />
                  {errors.email?.message && (
                    <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                      {errors.email?.message}
                    </p>
                  )}
                </label>
                <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                  Password
                  <Input {...register('password')} type="password" hasError={!!errors.password} />
                  {errors.password?.message && (
                    <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                      {errors.password?.message}
                    </p>
                  )}
                </label>
                <div className="relative flex flex-col w-full gap-7">
                  <div className="flex items-center justify-between w-full">
                    <label className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap">
                      <Input
                        type="checkbox"
                        className="cursor-pointer w-fit h-fit"
                        checked={rememberMe}
                        onChange={() => setRememberMe(!rememberMe)}
                      />
                      <p className="select-none text-dark-text">Remember me</p>
                    </label>

                    <a href="/forgot-password" className="font-normal text-primary hover:underline">
                      Forgot Password?
                    </a>
                  </div>

                  <CloudflareTurnstileWidget
                    type={CloudflareAction.LOGIN}
                    onValidationChange={setIsTurnstileValid}
                  />

                  <Button
                    disabled={isButtonDisabled}
                    variant="default"
                    type="submit"
                    className="flex items-center gap-0.5"
                  >
                    {isSubmitting && <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Sign in
                  </Button>
                  {loginError && (
                    <p className="absolute left-0 text-xs font-medium text-red-600 -top-4">
                      {loginError}
                    </p>
                  )}
                </div>
              </form>
            )}
            <p className="text-sm">
              Not a member yet?{' '}
              <NavLink to="/sign-up" className="font-medium text-primary hover:underline">
                Create an Account.
              </NavLink>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Login;
